import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { ref, get, set, update, remove } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import '../CSS/AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Form states
  const [formData, setFormData] = useState({
    // Project fields
    title: '',
    description: '',
    imageUrl: '',
    githubUrl: '',
    liveUrl: '',
    category: '',
    // Certificate fields
    issuer: '',
    pdfUrl: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'projects') {
        const projectsRef = ref(db, 'projects');
        const snapshot = await get(projectsRef);
        if (snapshot.exists()) {
          const projectsData = [];
          snapshot.forEach((childSnapshot) => {
            projectsData.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setProjects(projectsData);
        } else {
          setProjects([]);
        }
      } else {
        const certificatesRef = ref(db, 'certificates');
        const snapshot = await get(certificatesRef);
        if (snapshot.exists()) {
          const certificatesData = [];
          snapshot.forEach((childSnapshot) => {
            certificatesData.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setCertificates(certificatesData);
        } else {
          setCertificates([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      alert(`Error loading data: ${error.message}\n\nCheck console for details.`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        await signOut(auth);
        navigate('/admin/login', { replace: true });
      } catch (error) {
        console.error('Logout error:', error);
        alert('Error logging out. Please try again.');
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const generateNextId = (items, prefix) => {
    // Extract numeric parts from existing IDs
    const numbers = items
      .map(item => {
        const match = item.id.match(new RegExp(`${prefix}_(\\d+)`));
        return match ? parseInt(match[1]) : 0;
      })
      .filter(num => num > 0);
    
    // Find the highest number and add 1
    const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    
    // Format with leading zeros (e.g., 001, 002, etc.)
    return `${prefix}_${String(nextNumber).padStart(3, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const tableName = activeTab === 'projects' ? 'projects' : 'certificates';
      
      if (editingItem) {
        // Update existing item
        const itemRef = ref(db, `${tableName}/${editingItem.id}`);
        await update(itemRef, formData);
        alert('Updated successfully!');
      } else {
        // Add new item with sequential ID
        const prefix = activeTab === 'projects' ? 'project' : 'cert';
        const items = activeTab === 'projects' ? projects : certificates;
        const newId = generateNextId(items, prefix);
        
        const itemRef = ref(db, `${tableName}/${newId}`);
        await set(itemRef, formData);
        alert('Added successfully!');
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving data. Please try again.');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const tableName = activeTab === 'projects' ? 'projects' : 'certificates';
        const itemRef = ref(db, `${tableName}/${id}`);
        await remove(itemRef);
        alert('Deleted successfully!');
        fetchData();
      } catch (error) {
        console.error('Error deleting:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      imageUrl: '',
      githubUrl: '',
      liveUrl: '',
      category: '',
      issuer: '',
      pdfUrl: ''
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const renderProjectForm = () => (
    <form onSubmit={handleSubmit} className="admin-form">
      <h3>{editingItem ? 'Edit Project' : 'Add New Project'}</h3>
      
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Description *</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows="4"
          required
        />
      </div>

      <div className="form-group">
        <label>Image URL *</label>
        <input
          type="url"
          name="imageUrl"
          value={formData.imageUrl}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>GitHub URL</label>
        <input
          type="url"
          name="githubUrl"
          value={formData.githubUrl}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label>Live URL</label>
        <input
          type="url"
          name="liveUrl"
          value={formData.liveUrl}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label>Category *</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleInputChange}
          required
        >
          <option value="">Select Category</option>
          <option value="Web">Web</option>
          <option value="Mobile">Mobile</option>
          <option value="UXUI">UX/UI</option>
          <option value="Figma">Figma</option>
          <option value="Cybersecurity">Cybersecurity</option>
        </select>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-submit">
          {editingItem ? 'Update' : 'Add'} Project
        </button>
        <button type="button" onClick={resetForm} className="btn-cancel">
          Cancel
        </button>
      </div>
    </form>
  );

  const renderCertificateForm = () => (
    <form onSubmit={handleSubmit} className="admin-form">
      <h3>{editingItem ? 'Edit Certificate' : 'Add New Certificate'}</h3>
      
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Issuer *</label>
        <input
          type="text"
          name="issuer"
          value={formData.issuer}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Description *</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows="3"
          required
        />
      </div>

      <div className="form-group">
        <label>PDF URL *</label>
        <input
          type="url"
          name="pdfUrl"
          value={formData.pdfUrl}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Category *</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleInputChange}
          required
        >
          <option value="">Select Category</option>
          <option value="Networking">Networking</option>
          <option value="Cybersecurity">Cybersecurity</option>
          <option value="ML">ML</option>
          <option value="Programming">Programming</option>
          <option value="Cloud">Cloud</option>
        </select>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-submit">
          {editingItem ? 'Update' : 'Add'} Certificate
        </button>
        <button type="button" onClick={resetForm} className="btn-cancel">
          Cancel
        </button>
      </div>
    </form>
  );

  const renderProjectList = () => (
    <div className="items-grid">
      {projects.map((project) => (
        <div key={project.id} className="item-card">
          <img src={project.imageUrl} alt={project.title} className="item-image" />
          <div className="item-content">
            <h3>{project.title}</h3>
            <p className="item-category">{project.category}</p>
            <p className="item-description">{project.description}</p>
            <div className="item-links">
              {project.githubUrl && <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">GitHub</a>}
              {project.liveUrl && <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">Live Demo</a>}
            </div>
          </div>
          <div className="item-actions">
            <button onClick={() => handleEdit(project)} className="btn-edit">Edit</button>
            <button onClick={() => handleDelete(project.id)} className="btn-delete">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCertificateList = () => (
    <div className="items-grid">
      {certificates.map((certificate) => (
        <div key={certificate.id} className="item-card">
          <div className="item-content">
            <h3>{certificate.title}</h3>
            <p className="item-issuer">Issued by: {certificate.issuer}</p>
            <p className="item-category">{certificate.category}</p>
            <p className="item-description">{certificate.description}</p>
            <div className="item-links">
              {certificate.pdfUrl && <a href={certificate.pdfUrl} target="_blank" rel="noopener noreferrer">View Certificate</a>}
            </div>
          </div>
          <div className="item-actions">
            <button onClick={() => handleEdit(certificate)} className="btn-edit">Edit</button>
            <button onClick={() => handleDelete(certificate.id)} className="btn-delete">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout} className="btn-logout">Logout</button>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'projects' ? 'tab-active' : ''}
          onClick={() => { setActiveTab('projects'); setShowForm(false); resetForm(); }}
        >
          Projects
        </button>
        <button
          className={activeTab === 'certificates' ? 'tab-active' : ''}
          onClick={() => { setActiveTab('certificates'); setShowForm(false); resetForm(); }}
        >
          Certificates
        </button>
      </div>

      <div className="admin-content">
        {!showForm && (
          <div className="add-new-section">
            <button onClick={() => setShowForm(true)} className="btn-add-new">
              + Add New {activeTab === 'projects' ? 'Project' : 'Certificate'}
            </button>
          </div>
        )}

        {showForm && (
          <div className="form-container">
            {activeTab === 'projects' ? renderProjectForm() : renderCertificateForm()}
          </div>
        )}

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="items-container">
            {activeTab === 'projects' ? (
              projects.length > 0 ? renderProjectList() : <p className="no-items">No projects yet. Add your first one!</p>
            ) : (
              certificates.length > 0 ? renderCertificateList() : <p className="no-items">No certificates yet. Add your first one!</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
